import React, { useCallback, useMemo } from 'react';
import { Box, Link, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { Card } from 'common/ui';
import { Channels } from 'generated-graphql/graphql';
import { EmailFooterSetting } from './EmailFooterSetting';
import {
  AccessTime,
  ArrowForward,
  CheckCircleOutline,
  InfoOutlined
} from '@mui/icons-material';
import { DrawerComponentType } from 'services/drawer/types';
import { getSupportUrl } from 'core/utils/supportUrl';
import { Checkbox } from 'common/form';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  organizationAtom,
  organizationConfigAtom
} from 'services/organization/atom';
import { useDrawer } from 'services/drawer/hooks';
import { useUpsertOrganizationConfigMutation } from 'generated-graphql/hooks/apollo-hooks';
import {
  InitialWhatsAppTemplatesBatchStatus,
  TwilioSenderStatus
} from 'generated-graphql/types';

const innerContainer: SxProps = {
  mt: 2,
  display: 'flex',
  gap: 2,
  width: '100%'
};

const titleContainer: SxProps = {
  display: 'flex',
  alignItems: 'center',
  color: 'customGrey.dark'
};

const iconSuccessContainer: SxProps = {
  color: 'success.dark',
  mr: 1
};

const iconPendingContainer: SxProps = {
  color: 'customGrey.dark',
  mr: 1
};

const iconErrorContainer: SxProps = {
  color: 'error.main',
  mr: 1
};

const textContentStyle: SxProps = {
  ml: 6
};

export const ChannelsAvailability: React.FC = () => {
  const organization = useRecoilValue(organizationAtom);
  const [organizationConfig, setOrganizationConfig] = useRecoilState(
    organizationConfigAtom
  );
  const { openDrawer } = useDrawer();
  const isEmailChannelFallbackEnabled = true;

  const [upsertOrganizationConfig, { loading }] =
    useUpsertOrganizationConfigMutation();

  const phoneNumber = organization?.twilioPhoneNumber;
  const emailIdentity = organization?.emailIdentity;
  const twilioSenderStatus = organization?.twilioSenderStatus;
  const initialWhatsAppTemplatesBatchStatus =
    organization?.initialWhatsAppTemplatesBatchStatus;
  const isWhatsAppOffline =
    organization?.twilioSenderStatus === TwilioSenderStatus.Offline;
  const isSmsEnabled = organization?.channelsEnabled?.includes(Channels.Sms);
  const isEmailEnabled = organization?.channelsEnabled?.includes(
    Channels.Email
  );

  const handleEmailChannelFallbackEnabledChange = async () => {
    const currentEmailChannelFallbackEnabled =
      organizationConfig?.emailChannelFallbackEnabled ?? false;

    const { data } = await upsertOrganizationConfig({
      variables: {
        input: {
          emailChannelFallbackEnabled: !currentEmailChannelFallbackEnabled
        }
      }
    });

    const updatedOrganizationConfig = data?.upsertOrganizationConfig;

    if (updatedOrganizationConfig) {
      setOrganizationConfig((prevConfig) =>
        prevConfig
          ? {
              ...prevConfig,
              emailChannelFallbackEnabled:
                updatedOrganizationConfig.emailChannelFallbackEnabled
            }
          : updatedOrganizationConfig
      );
    }
  };

  const isWhatsAppEnabled = useMemo(() => {
    return (
      organization?.channelsEnabled?.includes(Channels.Whatsapp) &&
      organization?.twilioSenderStatus === TwilioSenderStatus.Online &&
      organization?.initialWhatsAppTemplatesBatchStatus ===
        InitialWhatsAppTemplatesBatchStatus.Approved
    );
  }, [organization]);

  const isWhatsAppPending = useMemo(() => {
    return (
      twilioSenderStatus === TwilioSenderStatus.Pending ||
      initialWhatsAppTemplatesBatchStatus ===
        InitialWhatsAppTemplatesBatchStatus.Pending
    );
  }, [initialWhatsAppTemplatesBatchStatus, twilioSenderStatus]);

  const getChannelIcon = useCallback(
    (channel: Channels) => {
      if (channel === Channels.Whatsapp && isWhatsAppPending) {
        return <AccessTime sx={iconPendingContainer} fontSize="large" />;
      }

      if (channel === Channels.Whatsapp && isWhatsAppEnabled) {
        return (
          <CheckCircleOutline sx={iconSuccessContainer} fontSize="large" />
        );
      }

      if (channel === Channels.Whatsapp && isWhatsAppOffline) {
        return <InfoOutlined sx={iconErrorContainer} fontSize="large" />;
      }

      if (channel === Channels.Sms && isSmsEnabled) {
        return (
          <CheckCircleOutline sx={iconSuccessContainer} fontSize="large" />
        );
      }

      if (channel === Channels.Email && isEmailEnabled) {
        return (
          <CheckCircleOutline sx={iconSuccessContainer} fontSize="large" />
        );
      }
    },
    [
      isEmailEnabled,
      isSmsEnabled,
      isWhatsAppEnabled,
      isWhatsAppOffline,
      isWhatsAppPending
    ]
  );

  return (
    <>
      <Box sx={innerContainer}>
        <Card
          title={
            <Box sx={titleContainer}>
              {getChannelIcon(Channels.Whatsapp)}
              WhatsApp
            </Box>
          }
        >
          {!isWhatsAppEnabled && !isWhatsAppPending && !isWhatsAppOffline && (
            <Button
              variant="tertiary"
              endIcon={<ArrowForward />}
              sx={{
                pl: 0
              }}
              onClick={() => {
                openDrawer({
                  drawerContentType: DrawerComponentType.WhatsAppSetup,
                  drawerComponentProps: {
                    hideCloseIconButton: true
                  }
                });
              }}
            >
              Set up
            </Button>
          )}
          {isWhatsAppPending && (
            <Typography sx={textContentStyle}>Setup in progress</Typography>
          )}
          {isWhatsAppEnabled && (
            <Typography sx={textContentStyle}>{phoneNumber} active</Typography>
          )}
          {isWhatsAppOffline && (
            <Typography sx={textContentStyle}>
              Issue with set up.{' '}
              <Link
                href={getSupportUrl({ ticketForm: 'help_with_whatsapp_setup' })}
                target="_blank"
              >
                Contact support
              </Link>
            </Typography>
          )}
        </Card>
        <Card
          title={
            <Box sx={titleContainer}>
              {getChannelIcon(Channels.Sms)}
              SMS
            </Box>
          }
        >
          {isSmsEnabled ? (
            <Typography sx={textContentStyle}>{phoneNumber} active</Typography>
          ) : (
            <Typography>
              <Link
                href={getSupportUrl({ ticketForm: 'help_with_sms_setup' })}
                target="_blank"
              >
                Contact support to set up
              </Link>
            </Typography>
          )}
        </Card>

        <Card
          title={
            <Box sx={titleContainer}>
              {getChannelIcon(Channels.Email)}
              Email
            </Box>
          }
        >
          {isEmailEnabled ? (
            <Typography sx={textContentStyle}>{emailIdentity}</Typography>
          ) : (
            <Typography>
              <Link
                href={getSupportUrl({ ticketForm: 'help_with_email_setup' })}
                target="_blank"
              >
                Contact support to set up
              </Link>
            </Typography>
          )}
        </Card>
      </Box>
      {isEmailChannelFallbackEnabled && (
        <Box sx={{ px: 0.5, py: 4 }}>
          <Checkbox
            value={organizationConfig?.emailChannelFallbackEnabled ?? false}
            onChange={handleEmailChannelFallbackEnabledChange}
            field={{
              checkboxLabel: `Use candidate's email if phone number fails (WhatsApp & SMS)`,
              disabled: loading
            }}
          />
        </Box>
      )}
      <EmailFooterSetting isEmailEnabled={!!isEmailEnabled} />
    </>
  );
};
